import * as THREE from 'three';
import { EnemyType, TargetConfiguration } from '@lucky-vr/common';
import { emissiveMaterial } from '../core/MaterialLibrary.js';

interface EnemyVisual {
  group: THREE.Group;
  type: EnemyType;
  flashMats: THREE.MeshStandardMaterial[];
  hitFlash: number;       // seconds of white flash remaining
  telegraph: number;      // seconds of warning glow remaining
  telegraphDuration: number;
  aimVisual: THREE.Group;
  aimBeam: THREE.Mesh;
  aimTarget: THREE.Vector3;
  aimQuaternion: THREE.Quaternion;
  rotors: THREE.Object3D[];
  wheels: THREE.Object3D[];
  healthBar: THREE.Group;
  healthFill: THREE.Mesh;
  componentMarkers: Map<string, THREE.Mesh>;
  configuration: string;
  disabled: boolean;
}

const HEALTH_BAR_WIDTH = 1.8;

/** Creates, animates and removes enemy meshes to mirror server state. */
export class EnemyManager {
  private visuals = new Map<string, EnemyVisual>();

  constructor(private parent: THREE.Group) {}

  /** Add visuals for new enemies, drop visuals for removed ones. */
  sync(ids: Set<string>, typeOf: (id: string) => string): void {
    for (const id of ids) {
      if (!this.visuals.has(id)) {
        this.add(id, typeOf(id) as EnemyType);
      }
    }
    for (const id of [...this.visuals.keys()]) {
      if (!ids.has(id)) this.remove(id);
    }
  }

  has(id: string): boolean {
    return this.visuals.has(id);
  }

  setPose(id: string, position: THREE.Vector3, quaternion: THREE.Quaternion): void {
    const v = this.visuals.get(id);
    if (!v) return;
    v.group.position.copy(position);
    v.group.quaternion.copy(quaternion);
  }

  setHealth(id: string, health: number, maxHealth: number): void {
    const v = this.visuals.get(id);
    if (!v) return;
    const ratio = Math.max(0, Math.min(1, health / maxHealth));
    v.healthBar.visible = ratio < 1;
    v.healthFill.scale.x = Math.max(0.001, ratio);
    v.healthFill.position.x = -HEALTH_BAR_WIDTH * (1 - ratio) * 0.5;
    const mat = v.healthFill.material as THREE.MeshBasicMaterial;
    mat.color.setHex(ratio > 0.5 ? 0x51cf66 : ratio > 0.25 ? 0xfcc419 : 0xff4444);
  }

  flashHit(id: string): void {
    const v = this.visuals.get(id);
    if (v) v.hitFlash = 0.1;
  }

  telegraph(
    id: string,
    durationMs: number,
    target: THREE.Vector3,
    targetQuaternion: THREE.Quaternion,
  ): void {
    const v = this.visuals.get(id);
    if (!v) return;
    v.telegraph = durationMs / 1000;
    v.telegraphDuration = v.telegraph;
    v.aimTarget.copy(target);
    v.aimQuaternion.copy(targetQuaternion);
    v.aimVisual.visible = true;
  }

  clearTelegraph(id: string): void {
    const v = this.visuals.get(id);
    if (!v) return;
    v.telegraph = 0;
    v.aimVisual.visible = false;
  }

  setTargetVisual(
    id: string,
    configuration: string,
    disabled: boolean,
    components: Array<{ id: string; exposed: boolean; broken: boolean }>,
  ): void {
    const visual = this.visuals.get(id);
    if (!visual) return;
    visual.configuration = configuration;
    visual.disabled = disabled;
    for (const [suffix, marker] of visual.componentMarkers) {
      const state = components.find((component) => component.id.endsWith(suffix));
      marker.visible = Boolean(state?.exposed && !state.broken);
      if (state?.broken) marker.scale.setScalar(0.01);
    }
  }

  /** Where this enemy's mesh center is (for muzzle flashes etc). */
  getPosition(id: string): THREE.Vector3 | null {
    const v = this.visuals.get(id);
    return v ? v.group.position.clone() : null;
  }

  update(dt: number, cameraPos: THREE.Vector3): void {
    for (const v of this.visuals.values()) {
      // spinning parts
      for (const rotor of v.rotors) rotor.rotation.y += 28 * dt;
      for (const wheel of v.wheels) wheel.rotation.x -= 14 * dt;

      // health bar faces the camera
      if (v.healthBar.visible) v.healthBar.lookAt(cameraPos);

      // emissive: white hit flash wins over orange telegraph pulse
      let emissive = 0x000000;
      let intensity = 0;
      if (v.hitFlash > 0) {
        v.hitFlash -= dt;
        emissive = 0xffffff;
        intensity = 0.9;
      } else if (v.telegraph > 0) {
        v.telegraph -= dt;
        emissive = 0xff4400;
        intensity = 0.4 + 0.4 * Math.abs(Math.sin(performance.now() * 0.02));
        const aimProgress = 1 - Math.max(0, v.telegraph) / Math.max(0.01, v.telegraphDuration);
        const beamOrigin = v.group.position.clone().add(new THREE.Vector3(0, 0.35, 0));
        const beamEnd = v.aimTarget.clone().add(new THREE.Vector3(0, 0.1, 0));
        const beamDelta = beamEnd.clone().sub(beamOrigin);
        const beamLength = Math.max(0.01, beamDelta.length());
        v.aimBeam.position.copy(beamOrigin).addScaledVector(beamDelta, 0.5);
        v.aimBeam.quaternion.setFromUnitVectors(
          new THREE.Vector3(0, 1, 0),
          beamDelta.normalize(),
        );
        v.aimBeam.scale.set(1, beamLength, 1);
        const beamMaterial = v.aimBeam.material as THREE.MeshBasicMaterial;
        const aimColor = aimProgress > 0.72 ? 0xff2f3d : 0xffb020;
        beamMaterial.color.setHex(aimColor);
        beamMaterial.opacity = 0.48 + aimProgress * 0.46;
        const marker = v.aimVisual.userData.marker as THREE.Group;
        const markerMaterials = v.aimVisual.userData.markerMaterials as THREE.MeshBasicMaterial[];
        markerMaterials.forEach((material) => material.color.setHex(aimColor));
        marker.position.copy(v.aimTarget);
        marker.quaternion.copy(v.aimQuaternion);
        const pulse = 1 + Math.sin(performance.now() * 0.024) * (0.08 + aimProgress * 0.12);
        marker.scale.setScalar(pulse);
        if (v.telegraph <= 0) v.aimVisual.visible = false;
      } else if (v.disabled) {
        emissive = 0xff3300;
        intensity = 0.18 + 0.08 * Math.abs(Math.sin(performance.now() * 0.006));
      } else if (v.configuration === TargetConfiguration.Critical) {
        emissive = 0xff2200;
        intensity = 0.2;
      } else if (v.configuration === TargetConfiguration.Exposed) {
        emissive = 0xff8a00;
        intensity = 0.12;
      }
      for (const mat of v.flashMats) {
        mat.emissive.setHex(emissive);
        mat.emissiveIntensity = intensity;
      }
    }
  }

  remove(id: string): void {
    const v = this.visuals.get(id);
    if (!v) return;
    this.parent.remove(v.group);
    this.parent.remove(v.aimVisual);
    v.aimBeam.geometry.dispose();
    (v.aimBeam.material as THREE.Material).dispose();
    v.aimVisual.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh) || obj === v.aimBeam) return;
      obj.geometry.dispose();
      const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
      materials.forEach((material) => material.dispose());
    });
    v.group.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        mats.forEach((m) => m.dispose());
      }
    });
    this.visuals.delete(id);
  }

  clear(): void {
    for (const id of [...this.visuals.keys()]) this.remove(id);
  }

  // -----------------------------------------------------------------
  // Mesh builders — villain palette: near-black bodies, orange accents
  // -----------------------------------------------------------------

  private add(id: string, type: EnemyType): void {
    const group = new THREE.Group();
    // air units face the player; ground units drive ahead showing their rear
    const body = new THREE.Group();
    if (type === EnemyType.Helicopter || type === EnemyType.Boss || type === EnemyType.Drone) {
      body.rotation.y = Math.PI;
    }
    group.add(body);

    const flashMats: THREE.MeshStandardMaterial[] = [];
    const rotors: THREE.Object3D[] = [];
    const wheels: THREE.Object3D[] = [];
    const componentMarkers = new Map<string, THREE.Mesh>();

    const mat = (color: number): THREE.MeshStandardMaterial => {
      const m = new THREE.MeshStandardMaterial({ color, roughness: 0.42, metalness: 0.38 });
      flashMats.push(m);
      return m;
    };

    // bright unlit marker so enemies read clearly at distance
    const addGlow = (w: number, h: number, x: number, y: number, z: number): void => {
      const glow = new THREE.Mesh(
        new THREE.BoxGeometry(w, h, 0.06),
        emissiveMaterial(0xff3b17, 4.5)
      );
      glow.position.set(x, y, z);
      body.add(glow);
    };

    let barHeight = 2.2;

    switch (type) {
      case EnemyType.Sedan: {
        const hull = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.55, 4.0), mat(0x23262e));
        hull.position.y = 0.55;
        hull.castShadow = true;
        body.add(hull);

        const cabin = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.5, 1.7), mat(0x171a20));
        cabin.position.set(0, 1.05, 0.1);
        body.add(cabin);

        const stripe = new THREE.Mesh(new THREE.BoxGeometry(1.84, 0.12, 4.02), mat(0x66ff41));
        stripe.position.y = 0.72;
        body.add(stripe);

        addGlow(1.5, 0.28, 0, 0.62, 2.01); // rear light bar
        this.addWheels(body, wheels, mat, 0.85, 0.32, 1.25);
        barHeight = 2.0;
        break;
      }

      case EnemyType.Motorcycle: {
        const frame = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.5, 2.0), mat(0x23262e));
        frame.position.y = 0.7;
        frame.castShadow = true;
        body.add(frame);

        const rider = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.8, 0.5), mat(0x30333c));
        rider.position.set(0, 1.35, 0.25);
        body.add(rider);

        const helmet = new THREE.Mesh(new THREE.SphereGeometry(0.2, 8, 6), mat(0x66ff41));
        helmet.position.set(0, 1.9, 0.25);
        body.add(helmet);

        addGlow(0.35, 0.25, 0, 0.75, 1.03); // tail light

        for (const z of [-0.75, 0.75]) {
          const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.35, 0.35, 0.18, 12), mat(0x111111));
          wheel.rotation.z = Math.PI / 2;
          wheel.position.set(0, 0.35, z);
          body.add(wheel);
          wheels.push(wheel);
        }
        barHeight = 2.4;
        break;
      }

      case EnemyType.Truck: {
        const cab = new THREE.Mesh(new THREE.BoxGeometry(2.3, 1.6, 2.0), mat(0x2a2118));
        cab.position.set(0, 1.3, -1.9);
        cab.castShadow = true;
        body.add(cab);

        const box = new THREE.Mesh(new THREE.BoxGeometry(2.4, 2.2, 4.0), mat(0x3a3128));
        box.position.set(0, 1.55, 1.2);
        box.castShadow = true;
        body.add(box);

        const stripe = new THREE.Mesh(new THREE.BoxGeometry(2.44, 0.3, 4.02), mat(0x66ff41));
        stripe.position.set(0, 1.55, 1.2);
        body.add(stripe);

        addGlow(2.0, 0.4, 0, 1.4, 3.21); // rear doors marker
        this.addWheels(body, wheels, mat, 1.05, 0.42, 1.7, -1.9);
        this.addWheels(body, wheels, mat, 1.05, 0.42, 0.4, 2.2);
        barHeight = 3.2;
        break;
      }

      case EnemyType.Drone: {
        // quadcopter: X-frame, four spinning rotors, glowing camera eye
        const core = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.3, 0.6), mat(0x23262e));
        core.castShadow = true;
        body.add(core);

        const eye = new THREE.Mesh(
          new THREE.SphereGeometry(0.14, 8, 6),
          emissiveMaterial(0xff3b17, 4.5)
        );
        eye.position.set(0, -0.05, -0.34);
        body.add(eye);

        for (const [ax, az] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
          const arm = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.06, 0.7), mat(0x30333c));
          arm.position.set(ax * 0.45, 0.1, az * 0.45);
          arm.rotation.y = Math.atan2(ax, az);
          body.add(arm);

          const rotor = this.buildRotor(0.42, mat(0x66ff41));
          rotor.position.set(ax * 0.68, 0.22, az * 0.68);
          body.add(rotor);
          rotors.push(rotor);
        }

        for (const side of [-1, 1]) {
          const skid = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.06, 0.7), mat(0x444444));
          skid.position.set(side * 0.3, -0.28, 0);
          body.add(skid);
        }
        barHeight = 1.1;
        break;
      }

      case EnemyType.Helicopter: {
        const hull = new THREE.Mesh(new THREE.BoxGeometry(1.6, 1.4, 3.6), mat(0x23262e));
        hull.castShadow = true;
        body.add(hull);

        addGlow(0.9, 0.35, 0, -0.4, -1.95); // cockpit glow facing the player

        const nose = new THREE.Mesh(new THREE.SphereGeometry(0.75, 8, 6), mat(0x171a20));
        nose.position.set(0, -0.1, -1.8);
        body.add(nose);

        const tail = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.4, 3.0), mat(0x23262e));
        tail.position.set(0, 0.3, 3.0);
        body.add(tail);

        const fin = new THREE.Mesh(new THREE.BoxGeometry(0.15, 1.0, 0.7), mat(0x66ff41));
        fin.position.set(0, 0.8, 4.3);
        body.add(fin);

        const rotor = this.buildRotor(3.2, mat(0x111111));
        rotor.position.y = 0.95;
        body.add(rotor);
        rotors.push(rotor);

        const tailRotor = this.buildRotor(0.9, mat(0x111111));
        tailRotor.rotation.z = Math.PI / 2;
        tailRotor.position.set(0.3, 0.8, 4.3);
        body.add(tailRotor);
        rotors.push(tailRotor);

        for (const side of [-1, 1]) {
          const skid = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.12, 2.6), mat(0x444444));
          skid.position.set(side * 0.8, -0.95, 0);
          body.add(skid);
        }
        barHeight = 1.8;
        break;
      }

      case EnemyType.Boss: {
        // Heavy gunship
        const hull = new THREE.Mesh(new THREE.BoxGeometry(3.4, 2.2, 7.0), mat(0x1c1f26));
        hull.castShadow = true;
        body.add(hull);

        const nose = new THREE.Mesh(new THREE.SphereGeometry(1.4, 8, 6), mat(0x14161c));
        nose.position.set(0, -0.2, -3.4);
        body.add(nose);

        // Glowing core — the "shoot me" spot
        const core = emissiveMaterial(0xff2200, 4.8);
        const eye = new THREE.Mesh(new THREE.SphereGeometry(0.55, 10, 8), core);
        eye.position.set(0, -0.3, -3.9);
        body.add(eye);

        for (const side of [-1, 1]) {
          const pod = new THREE.Mesh(new THREE.BoxGeometry(1.0, 1.0, 3.2), mat(0x2a2d36));
          pod.position.set(side * 2.4, -0.4, -0.5);
          body.add(pod);

          const gun = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 1.8, 8), mat(0x66ff41));
          gun.rotation.x = Math.PI / 2;
          gun.position.set(side * 2.4, -0.6, -2.4);
          body.add(gun);

          const rotor = this.buildRotor(2.6, mat(0x111111));
          rotor.position.set(side * 2.4, 1.4, 0.6);
          body.add(rotor);
          rotors.push(rotor);
        }

        const tail = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.7, 4.0), mat(0x1c1f26));
        tail.position.set(0, 0.5, 5.0);
        body.add(tail);

        const fin = new THREE.Mesh(new THREE.BoxGeometry(0.2, 1.6, 1.2), mat(0x66ff41));
        fin.position.set(0, 1.4, 6.4);
        body.add(fin);

        barHeight = 2.6;
        break;
      }

      case EnemyType.ArmoredTarget: {
        const isLimo = id.includes('magpie');
        const isRig = id.includes('wraith');
        const isDesert = id.includes('jackal');
        const isAmphibious = id.includes('kingfisher');
        const length = isRig ? 5.8 : isLimo ? 5.5 : isAmphibious ? 5.2 : 4.8;
        const accent = isLimo ? 0x2ee6d6
          : isRig ? 0xffd43b
          : isDesert ? 0xff8b32
          : isAmphibious ? 0x9cff57
          : 0x66ff41;

        // One shared hit-volume platform, with a different readable silhouette per operation.
        const chassis = new THREE.Mesh(new THREE.BoxGeometry(isRig ? 2.55 : 2.35, isDesert ? 0.78 : 0.55, length), mat(0x11151b));
        chassis.position.y = 0.58;
        chassis.castShadow = true;
        body.add(chassis);

        const armor = new THREE.Mesh(new THREE.BoxGeometry(isRig ? 2.35 : 2.12, isRig ? 1 : 0.72, isLimo ? 3.3 : 2.5), mat(0x252a32));
        armor.position.set(0, 1.02, -0.15);
        armor.castShadow = true;
        body.add(armor);

        const cabin = new THREE.Mesh(new THREE.BoxGeometry(isRig ? 1.9 : 1.65, isRig ? 0.7 : 0.45, isLimo ? 2.1 : 1.45), mat(0x0b1118));
        cabin.position.set(0, 1.55, -0.2);
        body.add(cabin);

        for (const side of [-1, 1]) {
          const ram = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.28, length - 0.15), mat(accent));
          ram.position.set(side * 1.13, 0.67, 0);
          body.add(ram);
        }

        const rearCore = new THREE.Mesh(
          new THREE.BoxGeometry(0.8, 0.42, 0.12),
          emissiveMaterial(0xff3300, 4.4),
        );
        rearCore.position.set(0, 0.95, length * 0.5 + 0.06);
        body.add(rearCore);
        addGlow(1.8, 0.22, 0, 0.55, length * 0.5 + 0.03);
        this.addWheels(body, wheels, mat, isRig ? 1.22 : 1.12, isDesert ? 0.52 : 0.4, length * 0.31);

        if (isRig) {
          const shieldBox = new THREE.Mesh(new THREE.BoxGeometry(2.3, 1.8, 2.1), mat(0x30343a));
          shieldBox.position.set(0, 1.65, 1.25);
          body.add(shieldBox);
        } else if (isDesert) {
          for (const side of [-1, 1]) {
            const cage = new THREE.Mesh(new THREE.BoxGeometry(0.13, 1.15, 3.5), mat(accent));
            cage.position.set(side * 1.05, 1.25, 0.3);
            body.add(cage);
          }
        } else if (isAmphibious) {
          for (const side of [-1, 1]) {
            const pontoon = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.42, 4.4, 10), mat(0x27352c));
            pontoon.rotation.x = Math.PI / 2;
            pontoon.position.set(side * 1.45, 0.48, 0.25);
            body.add(pontoon);
          }
        } else if (isLimo) {
          const roofWeapon = new THREE.Mesh(new THREE.BoxGeometry(0.75, 0.35, 1.1), mat(accent));
          roofWeapon.position.set(0, 1.92, 0.2);
          body.add(roofWeapon);
        }

        const addMarker = (suffix: string, x: number, y: number, z: number): void => {
          const marker = new THREE.Mesh(
            new THREE.SphereGeometry(0.5, 10, 8),
            new THREE.MeshBasicMaterial({
              color: 0xffd43b,
              wireframe: true,
              transparent: true,
              opacity: 0.9,
              depthTest: false,
            }),
          );
          marker.position.set(x, y, z);
          marker.visible = false;
          marker.renderOrder = 12;
          body.add(marker);
          componentMarkers.set(suffix, marker);
        };
        addMarker('_left_tire', -1.12, 0.42, 0.8);
        addMarker('_right_tire', 1.12, 0.42, 0.8);
        addMarker('_engine', 0, 0.95, length * 0.5 - 0.1);
        barHeight = isRig ? 3.1 : 2.45;
        break;
      }
    }

    // Floating health bar
    const healthBar = new THREE.Group();
    healthBar.position.y = barHeight;
    const bg = new THREE.Mesh(
      new THREE.PlaneGeometry(HEALTH_BAR_WIDTH + 0.12, 0.24),
      new THREE.MeshBasicMaterial({ color: 0x111111, transparent: true, opacity: 0.75, depthTest: false })
    );
    const fill = new THREE.Mesh(
      new THREE.PlaneGeometry(HEALTH_BAR_WIDTH, 0.15),
      new THREE.MeshBasicMaterial({ color: 0x51cf66, depthTest: false })
    );
    fill.position.z = 0.01;
    bg.renderOrder = 10;
    fill.renderOrder = 11;
    healthBar.add(bg);
    healthBar.add(fill);
    healthBar.visible = false;
    group.add(healthBar);

    // World-space lane lock: the beam and pulsing road marker show exactly
    // where this enemy will shoot once the orange aiming phase completes.
    const aimVisual = new THREE.Group();
    aimVisual.visible = false;
    const aimBeam = new THREE.Mesh(
      new THREE.CylinderGeometry(0.055, 0.09, 1, 8, 1, true),
      new THREE.MeshBasicMaterial({
        color: 0xffb020,
        transparent: true,
        opacity: 0.72,
        depthTest: false,
        depthWrite: false,
        toneMapped: false,
      }),
    );
    aimBeam.renderOrder = 18;
    aimVisual.add(aimBeam);

    const marker = new THREE.Group();
    const markerMaterial = new THREE.MeshBasicMaterial({
      color: 0xffb020,
      transparent: true,
      opacity: 0.82,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    });
    const markerMaterials = [markerMaterial];
    const laneMaterial = markerMaterial.clone();
    laneMaterial.opacity = 0.2;
    markerMaterials.push(laneMaterial);
    const lane = new THREE.Mesh(new THREE.PlaneGeometry(2.8, 14), laneMaterial);
    lane.rotation.x = -Math.PI / 2;
    lane.position.y = 0.015;
    lane.renderOrder = 18;
    marker.add(lane);
    const outer = new THREE.Mesh(new THREE.RingGeometry(1.15, 1.85, 28), markerMaterial);
    outer.rotation.x = -Math.PI / 2;
    outer.renderOrder = 19;
    marker.add(outer);
    for (const rotation of [Math.PI / 4, -Math.PI / 4]) {
      const slashMaterial = markerMaterial.clone();
      markerMaterials.push(slashMaterial);
      const slash = new THREE.Mesh(new THREE.PlaneGeometry(0.24, 3.5), slashMaterial);
      slash.rotation.set(-Math.PI / 2, 0, rotation);
      slash.position.y = 0.025;
      slash.renderOrder = 19;
      marker.add(slash);
    }
    aimVisual.add(marker);
    aimVisual.userData.marker = marker;
    aimVisual.userData.markerMaterials = markerMaterials;

    this.parent.add(group);
    this.parent.add(aimVisual);
    this.visuals.set(id, {
      group,
      type,
      flashMats,
      hitFlash: 0,
      telegraph: 0,
      telegraphDuration: 0,
      aimVisual,
      aimBeam,
      aimTarget: new THREE.Vector3(),
      aimQuaternion: new THREE.Quaternion(),
      rotors,
      wheels,
      healthBar,
      healthFill: fill,
      componentMarkers,
      configuration: '',
      disabled: false,
    });
  }

  private addWheels(
    group: THREE.Group,
    wheels: THREE.Object3D[],
    mat: (c: number) => THREE.MeshStandardMaterial,
    halfWidth: number,
    radius: number,
    zOffset: number,
    zCenter = 0
  ): void {
    for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      const wheel = new THREE.Mesh(
        new THREE.CylinderGeometry(radius, radius, 0.24, 12),
        mat(0x111111)
      );
      wheel.rotation.z = Math.PI / 2;
      wheel.position.set(sx * halfWidth, radius, zCenter + sz * zOffset);
      group.add(wheel);
      wheels.push(wheel);
    }
  }

  private buildRotor(radius: number, material: THREE.MeshStandardMaterial): THREE.Group {
    const rotor = new THREE.Group();
    for (let i = 0; i < 2; i++) {
      const blade = new THREE.Mesh(new THREE.BoxGeometry(radius * 2, 0.04, 0.22), material);
      blade.rotation.y = (i * Math.PI) / 2;
      rotor.add(blade);
    }
    const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.2, 8), material);
    rotor.add(hub);
    return rotor;
  }
}
